import { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import api, { monthlyMetricService } from '../../../services/api';
import { message } from 'antd';
import { prepareGridData } from '../utils/tableUtils';

/**
 * Metrik verilerini yükleyen ve yöneten hook
 * @returns {Object} Metriklerle ilgili veri ve fonksiyonlar
 */
const useMetricsData = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'));
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [allMetricsData, setAllMetricsData] = useState({});
  const [dailyMetricsData, setDailyMetricsData] = useState({});
  const [metrics, setMetrics] = useState({});
  const [gridData, setGridData] = useState({});
  // Tüm kampanyaları saklamak için yeni state
  const [allCampaigns, setAllCampaigns] = useState([]);
  
  // Verileri yüklenen markaların ID'lerini takip eden state
  const [fetchedBrandIds, setFetchedBrandIds] = useState([]);
  
  // Ref'ler oluşturuyoruz - bunlar state değişikliklerinde yeniden render'a neden olmaz
  const fetchedBrandIdsRef = useRef([]);
  const loadingRef = useRef(false);
  const selectMonthRef = useRef(selectedMonth);
  const metricsRef = useRef({});
  const allMetricsDataRef = useRef({});
  const pendingFetchRef = useRef(false); // Bekleyen istek var mı kontrolü için
  const timerRef = useRef(null); // Debounce için timer
  const campaignsLoadedRef = useRef(false); // Kampanyaların yüklenip yüklenmediğini takip etmek için
  
  // PickerOpen durumunu düzgün kontrol etmek için memoize edelim
  const handlePickerOpenChange = useCallback((open) => {
    if (open !== isPickerOpen) {
      setIsPickerOpen(open);
    }
  }, [isPickerOpen]);
  
  // State değişikliklerini ref'lere yansıtalım
  useEffect(() => {
    fetchedBrandIdsRef.current = fetchedBrandIds;
  }, [fetchedBrandIds]);
  
  useEffect(() => {
    loadingRef.current = false; // Loading state'i ref'imize yansıtılacak
  }, []);
  
  useEffect(() => {
    selectMonthRef.current = selectedMonth;
  }, [selectedMonth]);
  
  useEffect(() => {
    metricsRef.current = metrics;
  }, [metrics]);
  
  useEffect(() => {
    allMetricsDataRef.current = allMetricsData;
  }, [allMetricsData]);
  
  // Belirli markalar için verileri getir
  const fetchDataForBrands = useCallback(async (brandsToFetch, setLoading) => {
    // Eğer halihazırda yükleme yapılıyorsa veya hiç yüklenecek marka yoksa çık
    if (loadingRef.current || !brandsToFetch || brandsToFetch.length === 0 || pendingFetchRef.current) {
      return;
    }
    
    try {
      pendingFetchRef.current = true;
      setLoading(true);
      loadingRef.current = true;
      
      // Sadece daha önce verileri yüklenmemiş markaları filtrele
      const fetchedIds = fetchedBrandIdsRef.current;
      const brandsToLoad = brandsToFetch.filter(brand => !fetchedIds.includes(brand.id));
      
      // Yüklenecek marka yoksa işlemi sonlandır
      if (brandsToLoad.length === 0) {
        pendingFetchRef.current = false;
        setLoading(false);
        loadingRef.current = false;
        return;
      }
      
      // Markalar için metrikleri getir
      const newMetricsData = { ...metricsRef.current };
      const newAllMetricsData = { ...allMetricsDataRef.current };
      const newDailyData = { ...dailyMetricsData };
      const newGridData = { ...gridData };
      const newFetchedIds = [...fetchedIds];
      
      // Tüm istekleri birleştirip, paralel yürütme
      await Promise.all(brandsToLoad.map(async (brand) => {
        try {
          // 0. Marka bilgilerini al - pasif markaları dahil et
          const brandInfoResponse = await api.get(`/brands/${brand.id}`, {
            params: { includeInactive: 'true' }
          });
          const brandCurrency = brandInfoResponse.data.currency || 'TRY';
          
          // 1. Metrikleri getir - pasif markaları dahil et
          const metricResponse = await api.get(`/brands/${brand.id}/metrics`, {
            params: { includeInactive: 'true' }
          });
          // Sadece aktif ve görünür metrikleri filtrele
          const activeMetrics = metricResponse.data
            .filter(metric => metric.BrandMetric?.isActive && metric.BrandMetric?.isVisible !== false)
            .sort((a, b) => {
              const orderA = a.BrandMetric?.displayOrder || 0;
              const orderB = b.BrandMetric?.displayOrder || 0;
              return orderA - orderB;
            });
          
          newMetricsData[brand.id] = activeMetrics;
          
          // 2. Aylık metrik verilerini getir
          const values = await monthlyMetricService.getMonthlyMetricValues(
            brand.id,
            selectMonthRef.current.format('YYYY-MM')
          );
          
          // Normal displayOrder'a göre sırala - bu üst kartlarda kullanılacak
          const sortedValues = values
            .filter(metric => metric.isVisible === true)
            .sort((a, b) => {
              const orderA = a.displayOrder !== undefined ? a.displayOrder : Number.MAX_SAFE_INTEGER;
              const orderB = b.displayOrder !== undefined ? b.displayOrder : Number.MAX_SAFE_INTEGER;
              return orderA - orderB;
            });

          newAllMetricsData[brand.id] = {
            filteredMetrics: sortedValues, // Bu üst kartlarda kullanılıyor - displayOrder'a göre sıralanmış
            allMetrics: values, // Tüm metrikler - sıralanmamış
            brandInfo: {
              id: brand.id,
              name: brand.name,
              currency: brandCurrency
            }
          };
          
          // 3. Günlük verileri getir
          const startDate = selectMonthRef.current.startOf('month');
          const endDate = selectMonthRef.current.endOf('month');
          
          const dailyResponse = await api.get('/data', {
            params: {
              brandId: brand.id,
              startDate: startDate.format('YYYY-MM-DD'),
              endDate: endDate.format('YYYY-MM-DD')
            }
          });
          
          newDailyData[brand.id] = dailyResponse.data;
          
          // Grid verisini hazırla
          if (activeMetrics.length > 0) {
            newGridData[brand.id] = prepareGridData(
              brand.id, 
              dailyResponse.data, 
              newMetricsData, 
              newAllMetricsData,
              selectMonthRef.current
            );
          }
          
          // Yüklenen markanın ID'sini ekle
          newFetchedIds.push(brand.id);
        } catch (error) {
          console.error(`${brand.name} markası için veri yükleme hatası:`, error);
        }
      }));
      
      // State'leri tek seferde güncelle, gereksiz render'ları önlemek için
      setMetrics(newMetricsData);
      setAllMetricsData(newAllMetricsData);
      setDailyMetricsData(newDailyData);
      setGridData(newGridData);
      setFetchedBrandIds(newFetchedIds);
      
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      message.error('Veriler yüklenirken bir hata oluştu');
    } finally {
      pendingFetchRef.current = false;
      setLoading(false);
      loadingRef.current = false;
    }
  }, [dailyMetricsData, gridData]);
  
  // Ay değiştiğinde çalışacak fonksiyon
  const handleMonthChange = useCallback(async (date, brands, setLoading) => {
    // Eğer aynı ay seçildiyse işlem yapmayalım
    if (date.isSame(selectMonthRef.current, 'month')) {
      setIsPickerOpen(false);
      return;
    }
    
    // Verileri yüklemeden önce picker'ı kapatalım
    handlePickerOpenChange(false);
    
    // Ay değiştiğinde yeni ayı set edelim
    setSelectedMonth(date);
    
    // Varsa önceki zamanlayıcıyı temizle
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Ay değiştiğinde tüm verileri sıfırla
    setFetchedBrandIds([]);
    setMetrics({});
    setAllMetricsData({});
    setDailyMetricsData({});
    setGridData({});
    
    // Yeni API istekleri için gecikme ekle - UI'nin önce güncellenmesini sağla
    timerRef.current = setTimeout(() => {
      // Sadece görüntülenen markalar için veri getir
      if (brands && brands.length > 0) {
        fetchDataForBrands(brands, setLoading);
      }
    }, 300); // 300ms gecikme - UI güncellemesine zaman tanı
  }, [fetchDataForBrands, setIsPickerOpen]);
  
  // Tüm markalar için aylık metrikleri yeniden hesapla
  const handleRecalculateAllBrands = useCallback(async (brands, setLoading) => {
    try {
      setLoading(true);
      
      let successCount = 0;
      let errorCount = 0;
      
      // Sadece yüklenen markalar için hesaplama yap
      for (const brand of brands) {
        try {
          await monthlyMetricService.recalculateMonthlyMetrics(
            brand.id,
            selectedMonth.format('YYYY-MM-DD')
          );
          successCount++;
        } catch (error) {
          console.error(`${brand.name} markası için hesaplama hatası:`, error);
          errorCount++;
        }
      }
      
      if (errorCount === 0) {
        message.success(`${successCount} marka için aylık metrikler başarıyla güncellendi.`);
      } else {
        message.warning(`${successCount} marka güncellendi, ${errorCount} markada hata oluştu.`);
      }
      
      // Verileri yeniden yükle ve tüm markaları tekrar fetch edilecek olarak işaretle
      setFetchedBrandIds([]);
      fetchDataForBrands(brands, setLoading);
      
    } catch (error) {
      console.error('Toplu hesaplama hatası:', error);
      message.error('Aylık metrikler güncellenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, fetchDataForBrands]);
  
  // Tek bir marka için aylık metrikleri yeniden hesapla
  const handleRecalculateForBrand = useCallback(async (brandId, brandName, brands, setLoading) => {
    try {
      setLoading(true);
      
      const result = await monthlyMetricService.recalculateMonthlyMetrics(
        brandId,
        selectedMonth.format('YYYY-MM-DD')
      );
      
      if (result.success) {
        message.success(`${brandName} için aylık metrikler başarıyla güncellendi.`);
        
        // Bu markayı tekrar fetch edilecek olarak işaretle
        setFetchedBrandIds(prevIds => prevIds.filter(id => id !== brandId));
        
        // Sadece bu marka için verileri yeniden yükle
        const brand = brands.find(b => b.id === brandId);
        if (brand) {
          fetchDataForBrands([brand], setLoading);
        }
      } else {
        message.error(`${brandName} için güncelleme hatası: ${result.error}`);
      }
    } catch (error) {
      console.error(`${brandName} için hesaplama hatası:`, error);
      message.error(`${brandName} için aylık metrikler güncellenirken bir hata oluştu`);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, fetchDataForBrands]);

  // Component unmount olduğunda zamanlayıcıları temizle
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  
  // Seçilen ay için kampanyaları getir
  const fetchAllCampaigns = useCallback(async () => {
    if (campaignsLoadedRef.current && allCampaigns.length > 0) return;
    
    try {
      // Tarihleri belirleme
      const monthStart = selectMonthRef.current.startOf('month').format('YYYY-MM-DD');
      const monthEnd = selectMonthRef.current.endOf('month').format('YYYY-MM-DD');
      
      // API'den yalnızca belirli tarih aralığı için kampanyalar getir
      const response = await api.get('/campaigns', {
        params: {
          startDate: monthStart,
          endDate: monthEnd
        }
      });
      
      setAllCampaigns(response.data);
      campaignsLoadedRef.current = true;
    } catch (error) {
      console.error('Kampanyalar yüklenirken bir hata oluştu:', error);
      message.error('Kampanyalar yüklenirken hata oluştu');
    }
  }, []);
  
  // Ay değiştiğinde kampanyaları yeniden yükle
  useEffect(() => {
    campaignsLoadedRef.current = false;
    fetchAllCampaigns();
  }, [selectedMonth, fetchAllCampaigns]);
  
  // Hook'un dönüş değerleri
  return {
    selectedMonth,
    isPickerOpen,
    setIsPickerOpen: handlePickerOpenChange,
    allMetricsData,
    dailyMetricsData,
    metrics,
    gridData,
    fetchedBrandIds,
    fetchDataForBrands,
    handleMonthChange,
    handleRecalculateAllBrands,
    handleRecalculateForBrand,
    allCampaigns,
    fetchAllCampaigns
  };
};

export default useMetricsData; 